<template>
  <span
    class="app-image"
    :class="[
      attrs.class,
      wrapperClass,
      {
        'app-image--loading': showPlaceholder,
        'app-image--empty': !hasSrc || hasError,
        'app-image--natural': isNatural,
        'app-image--letterbox': isLetterbox,
      },
    ]"
    :style="wrapperStyle"
  >
    <span
      v-if="showPlaceholder"
      class="app-image__skeleton"
      aria-hidden="true"
    />

    <span
      v-if="!hasSrc || hasError"
      class="app-image__fallback"
      aria-hidden="true"
    >
      <UIcon name="i-heroicons-photo" class="app-image__fallback-icon" />
    </span>

    <NuxtImg
      v-else-if="optimizable"
      :key="resolvedSrc"
      ref="mediaRef"
      v-bind="forwardedAttrs"
      :src="resolvedSrc"
      :alt="alt"
      :width="width"
      :height="height"
      :sizes="sizes"
      :loading="loading"
      :fetchpriority="fetchpriority"
      :fit="fit"
      :quality="quality"
      :format="format"
      :placeholder="resolvedPlaceholder"
      :densities="densities"
      :widths="widths"
      :preset="preset"
      :preload="preload"
      :modifiers="imageModifiers"
      class="app-image__media"
      :class="{ 'app-image__media--ready': isLoaded }"
      :style="mediaStyle"
      @load="onLoad"
      @error="onError"
    />
    <img
      v-else
      :key="resolvedSrc"
      ref="mediaRef"
      v-bind="forwardedAttrs"
      :src="resolvedSrc"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      :fetchpriority="fetchpriority"
      decoding="async"
      class="app-image__media"
      :class="{ 'app-image__media--ready': isLoaded }"
      :style="mediaStyle"
      @load="onLoad"
      @error="onError"
    >
  </span>
</template>

<script setup lang="ts">
import { isRasterImageUrl } from '~/utils/imageHosts'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  width?: number | string
  height?: number | string
  sizes?: string
  loading?: 'lazy' | 'eager'
  fetchpriority?: 'high' | 'low' | 'auto'
  fit?: 'cover' | 'contain' | 'inside' | 'outside' | 'fill'
  /** When set, overrides the Nuxt Image preset/global quality. */
  quality?: number
  format?: string
  /** NuxtImg LQIP — default true. Pass false to disable. */
  placeholder?: boolean | string | number | [number, number, number, number]
  densities?: string
  /** Responsive width candidates for srcset, e.g. "150 300 400" */
  widths?: string | number[]
  preset?: string
  preload?: boolean
  wrapperClass?: string
  /** When false, always use a plain <img> (e.g. admin previews). */
  optimize?: boolean
  /**
   * fill = stretch into parent box with object-fit (default).
   * natural = image keeps its ratio (width 100%, height auto), no letterbox bg.
   */
  layout?: 'fill' | 'natural'
}>(), {
  alt: '',
  loading: 'lazy',
  fit: 'cover',
  format: 'webp',
  placeholder: true,
  optimize: true,
  layout: 'fill',
})

const attrs = useAttrs()
const { mediaUrl } = useMediaUrl()

const mediaRef = ref<{ $el?: HTMLImageElement } | HTMLImageElement | null>(null)
const isLoaded = ref(false)
const hasError = ref(false)

const resolvedSrc = computed(() => mediaUrl(props.src))
const hasSrc = computed(() => Boolean(resolvedSrc.value))
const optimizable = computed(() => props.optimize && isRasterImageUrl(resolvedSrc.value))
const resolvedPlaceholder = computed(() => props.placeholder)
const showPlaceholder = computed(() => hasSrc.value && !hasError.value && !isLoaded.value)
const isNatural = computed(() => props.layout === 'natural')
const isLetterbox = computed(() => !isNatural.value && (props.fit === 'contain' || props.fit === 'inside'))

/** Sharp/IPX defaults letterbox pad to black — force transparent for contain/inside. */
const imageModifiers = computed(() => {
  if (!isLetterbox.value) return undefined
  return { background: 'transparent' }
})

const objectFit = computed<'cover' | 'contain' | 'fill'>(() => {
  if (isNatural.value) return 'contain'
  if (props.fit === 'contain' || props.fit === 'inside') return 'contain'
  if (props.fit === 'fill') return 'fill'
  return 'cover'
})

const mediaStyle = computed(() => ({
  objectFit: objectFit.value,
}))

const wrapperStyle = computed(() => {
  if (isNatural.value) return undefined
  const w = Number(props.width)
  const h = Number(props.height)
  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return { aspectRatio: `${w} / ${h}` }
  }
  return undefined
})

const forwardedAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

let detachNativeListeners: (() => void) | null = null
let syncAttempts = 0
let syncTimer: ReturnType<typeof setTimeout> | null = null

function resolveImgEl(): HTMLImageElement | null {
  const raw = mediaRef.value as unknown
  if (!raw) return null
  if (raw instanceof HTMLImageElement) return raw
  if (typeof raw === 'object' && raw !== null && '$el' in raw) {
    const el = (raw as { $el?: unknown }).$el
    if (el instanceof HTMLImageElement) return el
    if (el instanceof Element) return el.querySelector('img')
  }
  return null
}

function clearSyncTimer() {
  if (syncTimer) {
    clearTimeout(syncTimer)
    syncTimer = null
  }
}

function markLoaded() {
  isLoaded.value = true
  hasError.value = false
  clearSyncTimer()
}

function markError() {
  isLoaded.value = false
  hasError.value = true
  clearSyncTimer()
}

function syncLoadedState() {
  const img = resolveImgEl()
  if (!img) return false

  if (img.complete && img.naturalWidth > 0) {
    markLoaded()
    return true
  }

  if (img.complete && img.naturalWidth === 0 && img.currentSrc) {
    markError()
    return true
  }

  return false
}

function attachNativeListeners() {
  detachNativeListeners?.()
  detachNativeListeners = null

  const img = resolveImgEl()
  if (!img) return

  const onNativeLoad = () => markLoaded()
  const onNativeError = () => markError()

  img.addEventListener('load', onNativeLoad)
  img.addEventListener('error', onNativeError)
  detachNativeListeners = () => {
    img.removeEventListener('load', onNativeLoad)
    img.removeEventListener('error', onNativeError)
  }

  if (typeof img.decode === 'function') {
    img.decode().then(() => {
      if (img.naturalWidth > 0) markLoaded()
    }).catch(() => {
      // decode can reject while the image is still loading; load/error handlers cover that.
    })
  }
}

function scheduleLoadSync() {
  clearSyncTimer()
  syncAttempts = 0

  const tick = () => {
    attachNativeListeners()
    if (syncLoadedState()) return

    syncAttempts += 1
    if (syncAttempts < 16) {
      syncTimer = setTimeout(tick, syncAttempts < 4 ? 32 : 120)
      return
    }

    // Fail open: never leave the gallery blank if the browser already has pixels.
    const img = resolveImgEl()
    if (img?.naturalWidth > 0) markLoaded()
    else if (img?.complete) markError()
    else markLoaded()
  }

  nextTick(tick)
}

watch(resolvedSrc, () => {
  isLoaded.value = false
  hasError.value = false
  scheduleLoadSync()
})

onMounted(() => {
  scheduleLoadSync()
})

onBeforeUnmount(() => {
  clearSyncTimer()
  detachNativeListeners?.()
  detachNativeListeners = null
})

function onLoad() {
  markLoaded()
}

function onError() {
  markError()
}
</script>

<style scoped>
.app-image {
  position: relative;
  display: block;
  overflow: hidden;
  isolation: isolate;
  background-color: #f3f4f6;
}

.app-image--natural,
.app-image--letterbox {
  /* Show parent bg-muted (light gray) through transparent IPX padding */
  background-color: transparent;
}

.app-image--letterbox.app-image--loading {
  background-color: #f3f4f6;
}

.app-image.hidden {
  display: none;
}

.app-image__skeleton {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    110deg,
    #f3f4f6 0%,
    #f3f4f6 35%,
    #e5e7eb 50%,
    #f3f4f6 65%,
    #f3f4f6 100%
  );
  background-size: 200% 100%;
  animation: app-image-shimmer 1.4s ease-in-out infinite;
}

.app-image--natural.app-image--loading {
  min-height: 12rem;
  background-color: transparent;
}

.app-image--natural .app-image__skeleton {
  border-radius: inherit;
  background: linear-gradient(
    110deg,
    transparent 0%,
    rgba(0, 0, 0, 0.04) 35%,
    rgba(0, 0, 0, 0.07) 50%,
    rgba(0, 0, 0, 0.04) 65%,
    transparent 100%
  );
  background-size: 200% 100%;
}

.app-image__fallback {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.app-image--natural .app-image__fallback {
  position: relative;
  min-height: 8rem;
}

.app-image__fallback-icon {
  width: 2rem;
  height: 2rem;
}

.app-image__media {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-position: center;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.app-image--natural .app-image__media {
  position: static;
  inset: auto;
  width: 100%;
  height: auto;
  max-width: 100%;
}

.app-image__media--ready {
  opacity: 1;
}

@keyframes app-image-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-image__skeleton {
    animation: none;
    background: #e5e7eb;
  }

  .app-image__media {
    transition: none;
  }
}
</style>
